import React, { useMemo, useState } from "react";
import { Checkbox, Radio } from "@mui/material";
import styles from "./DynamicList.module.css";
import { isNonEmptyArray } from "../../../../utils";
import { CustomTooltip } from "../../../tooltip";
import { useEffectOnce } from "../../../../hooks";
import { CircularLoader } from "../../../loaders";
import { translate } from "../../../../providers";
import { CustomTextField } from "../../../text-fields";

const DynamicList = ({
	disabledSearch = false,
	identifier,
	labelIdentifier,
	labelRenderer = null,
	onChange,
	request,
	singleSelection,
	value = [],
}) => {
	const [elements, setElements] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const valueElements = useMemo(() => {
		if (!value) {
			return [];
		}
		if (Array.isArray(value)) {
			return value;
		}
		if (typeof value === "object" && Array.isArray(value.elements)) {
			return value.elements;
		}
		return [];
	}, [value]);
	const filteredElements = useMemo(
		() => elements.filter((element) => element.value.toLowerCase().includes(searchValue.toLowerCase())),
		[elements, searchValue]
	);
	const allChecked = useMemo(
		() =>
			filteredElements.every((filteredElement) =>
				valueElements.find((v) => v[identifier] === filteredElement[identifier])
			),
		[valueElements, identifier, filteredElements]
	);
	useEffectOnce(
		() => {
			setIsLoading(true);
			request()
				.then((data) => {
					setElements(data.possibleValues);
				})
				.catch(console.error)
				.finally(setIsLoading(false));
		},
		[request, isLoading, elements],
		() => typeof request === "function" && !isLoading && !isNonEmptyArray(elements)
	);
	const handleSelect = (row) => {
		if (singleSelection) {
			// For single selection, return the string value directly
			onChange(row[labelIdentifier]);
		} else {
			let tempValue = [...valueElements];
			if (valueElements.some((v) => v[identifier] === row[identifier])) {
				tempValue = tempValue.filter((tValue) => tValue[identifier] !== row[identifier]);
			} else {
				tempValue = [...tempValue, row];
			}
			// If value was an object with is/matchAll, preserve that structure
			if (value && typeof value === "object" && !Array.isArray(value)) {
				onChange({
					...value,
					elements: tempValue,
				});
			} else {
				onChange(tempValue);
			}
		}
	};
	const handleSelectAll = () => {
		if (isNonEmptyArray(valueElements)) {
			// If value was an object with is/matchAll, preserve that structure
			if (value && typeof value === "object" && !Array.isArray(value)) {
				onChange({
					...value,
					elements: [],
				});
			} else {
				onChange([]);
			}
			return;
		}
		// If value was an object with is/matchAll, preserve that structure
		if (value && typeof value === "object" && !Array.isArray(value)) {
			onChange({
				...value,
				elements: filteredElements,
			});
		} else {
			onChange(filteredElements);
		}
	};
	const handleChangeSearchValue = (e) => {
		setSearchValue(e.target.value);
		// Clear selection but preserve is/matchAll structure if present
		if (value && typeof value === "object" && !Array.isArray(value)) {
			onChange({
				...value,
				elements: [],
			});
		} else {
			onChange([]);
		}
	};
	const rowRenderer = (row) => (
		<div key={row[identifier]} className={styles.list__row} role="presentation" onClick={() => handleSelect(row)}>
			{singleSelection ? (
				<Radio
					checked={value === row[labelIdentifier]}
					classes={{ root: styles.list__checkbox }}
					size="small"
				/>
			) : (
				<Checkbox
					checked={valueElements.some((v) => v[identifier] === row[identifier])}
					classes={{ root: styles.list__checkbox }}
					size="small"
				/>
			)}
			<CustomTooltip
				showWhenOverflow
				title={
					(labelRenderer && typeof labelRenderer === "function" && labelRenderer(row[labelIdentifier])) ||
					row[labelIdentifier]
				}
			>
				<span className={styles.list__rowLabel}>
					{(labelRenderer && typeof labelRenderer === "function" && labelRenderer(row[labelIdentifier])) ||
						row[labelIdentifier]}
				</span>
			</CustomTooltip>
		</div>
	);
	return (
		<div className={styles.container}>
			{!disabledSearch && (
				<CustomTextField
					className={styles.textfield}
					placeholder={translate("common:btn.search")}
					size="medium"
					value={searchValue}
					onChange={handleChangeSearchValue}
				/>
			)}
			{isLoading ? (
				<div className={styles.loaderContainer}>
					<CircularLoader size={25} />
				</div>
			) : (
				<>
					{!singleSelection && (
						<div className={styles.list__row} role="presentation" onClick={handleSelectAll}>
							<Checkbox
								checked={allChecked}
								classes={{ root: styles.list__checkbox }}
								indeterminate={
									valueElements?.length > 0 && valueElements?.length < filteredElements?.length
								}
								size="small"
							/>
							<CustomTooltip showWhenOverflow title={translate("common:btn.select-all")}>
								<span className={styles.list__rowLabel}>{translate("common:btn.select-all")}</span>
							</CustomTooltip>
						</div>
					)}
					{isNonEmptyArray(filteredElements) && filteredElements.map((element) => rowRenderer(element))}
				</>
			)}
		</div>
	);
};

export default DynamicList;
